import { useMemo, useState } from "react";
import { Plus, Settings, Handshake, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { useDeals } from "@/hooks/useDeals";
import { useDealStages } from "@/hooks/useDealStages";
import { useSalesFunnels } from "@/hooks/useSalesFunnels";
import { useDealsFunnelMemberships } from "@/hooks/useDealsFunnelMemberships";
import {
  useDealsFunnelLeadsWithoutDeal,
  type DealsFunnelLeadPlaceholder,
} from "@/hooks/useDealsFunnelLeadsWithoutDeal";
import { useWorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";
import { filterDealsByFunnel } from "@/lib/deals-funnel-filter";
import { resolveFunnelDisplayName } from "@/lib/funnel-display-label";
import { DealsKanban } from "@/components/deals/DealsKanban";
import { DealDialog } from "@/components/deals/DealDialog";
import { DealDetailDrawer } from "@/components/deals/DealDetailDrawer";
import { DealStageManagerDialog } from "@/components/deals/DealStageManagerDialog";
import { DealsInsightsPanel } from "@/components/deals/DealsInsightsPanel";
import { FunnelDropdownMenu } from "@/components/leads/FunnelDropdownMenu";
import type { DealWithStage } from "@/types/deal";

const Deals = () => {
  const { user } = useAuth();
  const { workspaceId, loading: wsLoading } = useWorkspace(user?.id);
  const { memberId } = useMemberPermissions();
  const { deals, moveDealStage, updateDeal, deleteDeal } = useDeals(workspaceId);
  const { stages } = useDealStages(workspaceId);
  const { funnels, loading: funnelsLoading } = useSalesFunnels(workspaceId);
  /** null = todos os funis (comportamento legado da página antes do filtro por funil). */
  const [funnelFilterId, setFunnelFilterId] = useState<string | null>(null);
  const integrationById = useWorkspaceIntegrationsMap(workspaceId);
  const activeFunnel = funnelFilterId ? funnels.find(f => f.id === funnelFilterId) : undefined;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [stageManagerOpen, setStageManagerOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<DealWithStage | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dealDialogLead, setDealDialogLead] = useState<DealsFunnelLeadPlaceholder | null>(null);

  /** Inclui abertas, ganhas e perdidas — cards permanecem nas colunas Ganho e Perdido após fechar. */
  const kanbanDealsAll = useMemo(
    () => deals.filter(d => d.status === "open" || d.status === "won" || d.status === "lost"),
    [deals],
  );

  const { data: membershipLeadIds = new Set<string>() } = useDealsFunnelMemberships(
    workspaceId,
    funnelFilterId,
    activeFunnel,
  );

  const kanbanDeals = useMemo(
    () => filterDealsByFunnel(kanbanDealsAll, funnelFilterId, activeFunnel, membershipLeadIds),
    [kanbanDealsAll, funnelFilterId, activeFunnel, membershipLeadIds],
  );

  const dealLeadIdsWithAnyKanbanDeal = useMemo(() => {
    const ids = new Set<string>();
    kanbanDealsAll.forEach(d => {
      if (d.lead_id) ids.add(d.lead_id);
    });
    return ids;
  }, [kanbanDealsAll]);

  const { data: leadPlaceholders = [] } = useDealsFunnelLeadsWithoutDeal(
    workspaceId,
    funnelFilterId,
    activeFunnel,
    dealLeadIdsWithAnyKanbanDeal,
  );

  const openCount = kanbanDeals.filter(d => d.status === "open").length;

  const handleDealClick = (deal: DealWithStage) => {
    setSelectedDeal(deal);
    setDrawerOpen(true);
  };

  const handleMoveDeal = (dealId: string, toStageId: string) => {
    moveDealStage.mutate({ dealId, toStageId, memberId: memberId ?? undefined });
  };

  const handleLeadPlaceholderClick = (lead: DealsFunnelLeadPlaceholder) => {
    setDealDialogLead(lead);
    setDialogOpen(true);
  };

  const handleDealDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) setDealDialogLead(null);
  };

  if (wsLoading || !workspaceId || funnelsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center relative">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary to-primary/50 animate-pulse mx-auto mb-4" />
          <Sparkles className="w-6 h-6 text-primary absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <Handshake className="w-7 h-7 text-primary" />
              Negociações
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {activeFunnel
                ? `${resolveFunnelDisplayName(activeFunnel, integrationById)} · `
                : funnelFilterId === null && funnels.length > 0
                  ? "Todos os funis · "
                  : ""}
              {openCount} {openCount === 1 ? "aberta" : "abertas"}
              {leadPlaceholders.length > 0
                ? ` · ${leadPlaceholders.length} lead${leadPlaceholders.length === 1 ? "" : "s"} sem negociação`
                : ""}
              · ganhos e perdidos seguem no funil
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <FunnelDropdownMenu
              funnels={funnels}
              activeFunnelId={funnelFilterId}
              onSelectFunnel={setFunnelFilterId}
              includeAllFunnelsOption
              onOpenManager={() => {}}
              onOpenMoveLeads={() => {}}
              loading={funnelsLoading}
              integrationById={integrationById}
              showManagementActions={false}
            />
            <Button variant="outline" size="sm" onClick={() => setStageManagerOpen(true)}>
              <Settings className="w-4 h-4 mr-1" /> Estágios
            </Button>
            <Button size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Nova Negociação
            </Button>
          </div>
        </div>

        <DealsInsightsPanel workspaceId={workspaceId} />

        <DealsKanban
          deals={kanbanDeals}
          stages={stages}
          leadPlaceholders={leadPlaceholders}
          onDealClick={handleDealClick}
          onMoveDeal={handleMoveDeal}
          onLeadPlaceholderClick={handleLeadPlaceholderClick}
        />
      </div>

      <DealDialog
        open={dialogOpen}
        onOpenChange={handleDealDialogOpenChange}
        workspaceId={workspaceId!}
        stages={stages}
        memberId={memberId}
        onCreated={() => setDealDialogLead(null)}
        initialLeadId={dealDialogLead?.id}
        initialLeadName={dealDialogLead?.name}
        lockLead={!!dealDialogLead}
      />

      <DealDetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        deal={selectedDeal}
        stages={stages}
        workspaceId={workspaceId!}
        memberId={memberId}
        onUpdate={(id, updates) => updateDeal.mutate({ id, ...updates })}
        onMoveDealStage={(dealId, toStageId, mid) => moveDealStage.mutate({ dealId, toStageId, memberId: mid })}
        onDelete={id => deleteDeal.mutate(id)}
      />

      <DealStageManagerDialog
        open={stageManagerOpen}
        onOpenChange={setStageManagerOpen}
        workspaceId={workspaceId!}
      />
    </div>
  );
};

export default Deals;
